import { useEffect, useState } from 'react'
import { colors, spacing, fontSize, radius } from '../styles/theme'
import { useIsMobile } from '../hooks/useMediaQuery'

// ── Shared primitives ────────────────────────────────────────────────────────

function Code({ children }: { children: string }) {
  return (
    <code style={{
      fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
      fontSize: '13px',
      background: '#0A1413',
      color: '#93c5fd',
      padding: '2px 6px',
      borderRadius: radius.sm,
    }}>{children}</code>
  )
}

function CodeBlock({ children, language = '' }: { children: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(children.trim())
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div style={{ position: 'relative', margin: `${spacing.md} 0` }}>
      {language && (
        <div style={{
          fontSize: fontSize.xs,
          color: '#64748b',
          background: '#0A1413',
          padding: '4px 14px',
          borderRadius: `${radius.md} ${radius.md} 0 0`,
          fontFamily: 'ui-monospace, monospace',
          borderBottom: '1px solid #334155',
        }}>{language}</div>
      )}
      <pre style={{
        margin: 0,
        padding: `${spacing.md} 48px ${spacing.md} ${spacing.md}`,
        background: '#0A1413',
        color: '#e2e8f0',
        borderRadius: language ? `0 0 ${radius.md} ${radius.md}` : radius.md,
        fontSize: '13px',
        fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
        overflowX: 'auto',
        lineHeight: 1.6,
        whiteSpace: 'pre',
      }}>
        <code>{children.trim()}</code>
      </pre>
      <button
        onClick={copy}
        style={{
          position: 'absolute',
          top: language ? '30px' : '8px',
          right: '8px',
          padding: '3px 8px',
          background: copied ? colors.success : '#334155',
          color: '#e2e8f0',
          border: 'none',
          borderRadius: radius.sm,
          fontSize: fontSize.xs,
          cursor: 'pointer',
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: spacing.xxl }}>
      <h2 style={{
        fontSize: fontSize.xl,
        fontWeight: 700,
        color: colors.text,
        margin: `0 0 ${spacing.md}`,
        paddingBottom: spacing.sm,
        borderBottom: `2px solid ${colors.border}`,
      }}>{title}</h2>
      {children}
    </section>
  )
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} style={{ marginBottom: spacing.lg }}>
      <h3 style={{
        fontSize: fontSize.lg,
        fontWeight: 600,
        color: colors.text,
        margin: `${spacing.lg} 0 ${spacing.sm}`,
      }}>{title}</h3>
      {children}
    </div>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ color: colors.textSecondary, lineHeight: 1.7, margin: `0 0 ${spacing.md}` }}>{children}</p>
}

function Callout({ type, children }: { type: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const bg = type === 'warning' ? colors.warningLight : colors.primaryLight
  const border = type === 'warning' ? colors.warning : colors.primary
  const label = type === 'warning' ? 'Warning' : type === 'tip' ? 'Tip' : 'Note'
  return (
    <div style={{
      border: `1px solid ${border}`,
      borderLeft: `4px solid ${border}`,
      background: bg,
      borderRadius: radius.md,
      padding: `${spacing.sm} ${spacing.md}`,
      margin: `${spacing.md} 0`,
      fontSize: fontSize.base,
      color: colors.text,
      lineHeight: 1.6,
    }}>
      <strong>{label}: </strong>{children}
    </div>
  )
}

function Keyword({ word, description }: { word: string; description: string }) {
  return (
    <div style={{
      display: 'flex',
      gap: spacing.md,
      alignItems: 'flex-start',
      padding: `${spacing.sm} 0`,
      borderBottom: `1px solid ${colors.border}`,
    }}>
      <code style={{
        fontFamily: 'ui-monospace, monospace',
        fontSize: '13px',
        background: '#0A1413',
        color: '#93c5fd',
        padding: '3px 10px',
        borderRadius: radius.sm,
        whiteSpace: 'nowrap',
        minWidth: 200,
        display: 'inline-block',
      }}>{word}</code>
      <span style={{ color: colors.textSecondary, lineHeight: 1.6 }}>{description}</span>
    </div>
  )
}

// ── Table of contents ────────────────────────────────────────────────────────

const TOC = [
  { id: 'overview',        label: 'Overview' },
  { id: 'quickstart',      label: 'Quick Start' },
  { id: 'blocks',          label: 'Building with blocks' },
  { id: 'delimiters',      label: 'Delimiters' },
  { id: 'normalization',   label: 'Normalization' },
  { id: 'scalars',         label: 'Scalar Variables' },
  { id: 'nested',          label: 'Nested Objects' },
  { id: 'loops',           label: 'Repeating Sections' },
  { id: 'conditionals',    label: 'Conditionals' },
  { id: 'reserved',        label: 'Reserved Keywords' },
  { id: 'formatters',      label: 'Formatters' },
  { id: 'fmt-string',      label: '  Text' },
  { id: 'fmt-date',        label: '  Date' },
  { id: 'fmt-number',      label: '  Number & Currency' },
  { id: 'fmt-agg',         label: '  Array' },
  { id: 'fmt-image',       label: '  Image & Charts' },
  { id: 'fmt-migration',   label: '  Vendor migration' },
  { id: 'localization',    label: 'Localization' },
  { id: 'payload',         label: 'Payload Format' },
  { id: 'examples',        label: 'Examples' },
  { id: 'ex-invoice',      label: '  Invoice' },
  { id: 'ex-letter',       label: '  Business Letter' },
  { id: 'ex-conditional',  label: '  Conditional Sections' },
  { id: 'tips',            label: 'Tips & Best Practices' },
]

// ── Page ─────────────────────────────────────────────────────────────────────

export default function TemplateDocsPage() {
  const isMobile = useIsMobile()
  const [activeSection, setActiveSection] = useState('overview')
  const [tocOpen, setTocOpen] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveSection(e.target.id)
        }
      },
      { rootMargin: '-20% 0% -70% 0%' }
    )
    document.querySelectorAll('section[id], div[id]').forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '220px 1fr',
        gap: spacing.xl,
        padding: isMobile ? `${spacing.lg} ${spacing.md}` : `${spacing.xl} ${spacing.xl}`,
        alignItems: 'start',
      }}>
        {/* Sidebar TOC — desktop only */}
        {!isMobile && (
          <nav style={{
            position: 'sticky',
            top: 72,
            padding: spacing.md,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
          }}>
            <div style={{ fontSize: fontSize.xs, fontWeight: 700, color: colors.textMuted, marginBottom: spacing.sm, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Contents
            </div>
            {TOC.map(item => (
              <a
                key={item.id}
                href={`#${item.id}`}
                style={{
                  display: 'block',
                  padding: `4px ${item.label.startsWith('  ') ? spacing.lg : spacing.sm}`,
                  fontSize: fontSize.sm,
                  color: activeSection === item.id ? colors.primary : colors.textSecondary,
                  textDecoration: 'none',
                  borderRadius: radius.sm,
                  background: activeSection === item.id ? colors.primaryLight : 'transparent',
                  fontWeight: activeSection === item.id ? 600 : 400,
                  marginBottom: 2,
                }}
              >
                {item.label.trim()}
              </a>
            ))}
            <div style={{ marginTop: spacing.lg, paddingTop: spacing.md, borderTop: `1px solid ${colors.border}` }}>
              <a
                href="/demo"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  padding: `${spacing.sm} ${spacing.sm}`,
                  background: colors.primary,
                  color: '#fff',
                  borderRadius: radius.sm,
                  textAlign: 'center',
                  textDecoration: 'none',
                  fontSize: fontSize.sm,
                  fontWeight: 600,
                }}
              >
                Try it live ↗
              </a>
            </div>
          </nav>
        )}

        {/* Content */}
        <main style={{ minWidth: 0 }}>
          {/* Mobile collapsible TOC */}
          {isMobile && (
            <div style={{ marginBottom: spacing.lg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
              <button
                onClick={() => setTocOpen(o => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: colors.surface,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                <span>Contents</span>
                <span style={{ fontSize: '10px', color: colors.textMuted }}>{tocOpen ? '▲' : '▼'}</span>
              </button>
              {tocOpen && (
                <div style={{ background: colors.surface, borderTop: `1px solid ${colors.border}`, padding: `${spacing.sm} ${spacing.md}` }}>
                  {TOC.map(item => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={() => setTocOpen(false)}
                      style={{
                        display: 'block',
                        padding: `6px ${item.label.startsWith('  ') ? spacing.lg : spacing.sm}`,
                        fontSize: fontSize.sm,
                        color: activeSection === item.id ? colors.primary : colors.textSecondary,
                        textDecoration: 'none',
                        fontWeight: activeSection === item.id ? 600 : 400,
                      }}
                    >
                      {item.label.trim()}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ marginBottom: spacing.xl }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.sm}` }}>
              Template Writing Guide
            </h1>
            <p style={{ fontSize: fontSize.lg, color: colors.textSecondary, margin: 0 }}>
              Learn how to create Word templates that DocMorphia can populate with dynamic data.
            </p>
          </div>

          <Section id="overview" title="Overview">
            <P>
              A DocMorphia template is a standard Microsoft Word (<Code>.docx</Code>) file with{' '}
              <strong>variable placeholders</strong> embedded in the text. When you submit the template
              along with a JSON payload of values, DocMorphia replaces every placeholder with the
              corresponding value and renders the result as a PDF.
            </P>
            <P>
              Placeholders are written using <strong>double curly braces</strong>:{' '}
              <Code>{'{{variableName}}'}</Code>. Everything else in your Word document — fonts,
              tables, images, headers, footers, styles — is preserved exactly as designed.
            </P>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: spacing.md,
              margin: `${spacing.md} 0`,
            }}>
              <div style={{ padding: spacing.md, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
                <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.sm }}>Word template (.docx)</div>
                <div style={{ fontFamily: 'Georgia, serif', lineHeight: 1.8, color: colors.text }}>
                  <div>Dear <span style={{ background: '#dbeafe', padding: '0 3px', borderRadius: 2, fontFamily: 'monospace', fontSize: '12px' }}>{'{{salutation}}'}</span>,</div>
                  <div style={{ marginTop: 8 }}>Your order <span style={{ background: '#dbeafe', padding: '0 3px', borderRadius: 2, fontFamily: 'monospace', fontSize: '12px' }}>{'{{orderNumber}}'}</span> has shipped.</div>
                  <div style={{ marginTop: 8 }}>Expected delivery: <span style={{ background: '#dbeafe', padding: '0 3px', borderRadius: 2, fontFamily: 'monospace', fontSize: '12px' }}>{'{{deliveryDate}}'}</span>.</div>
                </div>
              </div>
              <div style={{ padding: spacing.md, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
                <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.sm }}>Rendered PDF output</div>
                <div style={{ fontFamily: 'Georgia, serif', lineHeight: 1.8, color: colors.text }}>
                  <div>Dear Alice Johnson,</div>
                  <div style={{ marginTop: 8 }}>Your order <strong>#ORD-2024-0512</strong> has shipped.</div>
                  <div style={{ marginTop: 8 }}>Expected delivery: January 20, 2025.</div>
                </div>
              </div>
            </div>
            <P>
              Want to follow along interactively?{' '}
              <a href="/demo" target="_blank" rel="noopener noreferrer"
                style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
                Open the live demo ↗
              </a>
              {' '}— pick a sample template, edit the values, and generate a PDF in real time.
            </P>
          </Section>

          <Section id="blocks" title="Building with blocks">
            <P>
              You don't need Word to build a template. Start from a layout — invoice, quote, offer letter, NDA,
              certificate, receipt, statement or report — on the New Template page, then shape it in the editor:
            </P>
            <ul style={{ color: colors.textSecondary, lineHeight: 2, paddingLeft: spacing.lg }}>
              <li><strong>Blocks</strong> — drag a heading, letterhead, line-items table, totals box, signature line,
                conditional or repeating section from the Blocks panel, or click to insert it after the cursor.</li>
              <li><strong>Data-bound blocks</strong> — a line-items table, totals box or repeating section asks which list
                and fields to use, straight from your template's sample data, and sets up the loop and formatting for you.
                Use <em>Edit binding…</em> on the block's handle to change it later.</li>
              <li><strong>Fields</strong> — drag a field from the Fields panel to drop in its placeholder; drag a list to get a
                repeating section. Click any placeholder to change its name or add formatters such as currency or dates.</li>
              <li><strong>Rearrange</strong> — hover a block and drag its ⋮⋮ handle, or use Alt+Shift+↑/↓.</li>
              <li><strong>Team blocks</strong> — select content and choose <em>Save as block</em> to reuse it across the
                team's templates. Inserting a block copies it, so later edits to the block don't change templates that
                already use it. Manage them on the Blocks page.</li>
            </ul>
          </Section>

          <Section id="quickstart" title="Quick Start">
            <ol style={{ color: colors.textSecondary, lineHeight: 2.2, paddingLeft: spacing.lg }}>
              <li>
                Open Microsoft Word and create your document with the design you want.
              </li>
              <li>
                Wherever you want dynamic content, type <Code>{'{{variableName}}'}</Code> —
                for example <Code>{'{{firstName}}'}</Code> or <Code>{'{{invoiceTotal}}'}</Code>.
              </li>
              <li>
                Save the file as <Code>.docx</Code> (not <Code>.doc</Code>).
              </li>
              <li>
                In DocMorphia, go to <strong>Templates → New Template</strong>, name it, then
                upload your <Code>.docx</Code> file.
              </li>
              <li>
                DocMorphia will parse the file and list every variable it discovered. Review
                them to confirm the parsing is correct.
              </li>
              <li>
                Use the <strong>Preview</strong> button to fill in test values and see the
                rendered PDF immediately.
              </li>
            </ol>
            <Callout type="tip">
              Variable names are case-insensitive and underscore-tolerant. You can write{' '}
              <Code>{'{{first_name}}'}</Code>, <Code>{'{{firstName}}'}</Code>, or{' '}
              <Code>{'{{FIRSTNAME}}'}</Code> — they all resolve to the same JSON key.
            </Callout>
          </Section>

          <Section id="delimiters" title="Delimiters">
            <P>
              Every placeholder must be wrapped in <strong>double curly braces</strong>:
            </P>
            <CodeBlock language="Word template">{`{{variableName}}`}</CodeBlock>
            <P>
              The opening delimiter is <Code>{'{{'}</Code> and the closing delimiter is{' '}
              <Code>{'}}'}</Code>. There must be no spaces between the delimiters and the
              variable name, though internal spaces in a name are ignored during normalization.
            </P>
            <P>
              Variable names can contain letters, numbers, underscores, dots (for nested
              paths), and hyphens. Structural directives use the same delimiters with a{' '}
              <Code>#</Code> or <Code>/</Code> prefix: <Code>{'{{#each items}}'}</Code>,{' '}
              <Code>{'{{/each}}'}</Code>.
            </P>
          </Section>

          <Section id="normalization" title="Normalization">
            <P>
              DocMorphia normalizes variable names before matching them against your JSON payload.
              Two names are considered identical if they produce the same normalized form.
            </P>
            <P>Normalization rules:</P>
            <ul style={{ color: colors.textSecondary, lineHeight: 2, paddingLeft: spacing.lg }}>
              <li>Converted to <strong>lowercase</strong></li>
              <li><strong>Underscores removed</strong></li>
              <li><strong>Hyphens removed</strong></li>
              <li><strong>Spaces removed</strong></li>
              <li>Dots (path separators) are preserved</li>
            </ul>
            <CodeBlock language="Normalization examples">{`{{firstName}}        → "firstname"
{{first_name}}       → "firstname"
{{FIRST_NAME}}       → "firstname"
{{First-Name}}       → "firstname"
{{contact.city}}     → "contact.city"   (dot preserved)
{{contact.City}}     → "contact.city"`}</CodeBlock>
            <P>
              This means you can use whatever casing convention feels natural in your templates
              and your JSON payload — they will match as long as the letters are the same.
            </P>
            <Callout type="warning">
              If two placeholders in the same template normalize to the <em>same</em> key but were
              spelled differently — e.g. <Code>{'{{first_name}}'}</Code> and{' '}
              <Code>{'{{firstName}}'}</Code> — DocMorphia will reject the upload with a clash
              error. Pick one spelling per variable and use it consistently.
            </Callout>
          </Section>

          <Section id="scalars" title="Scalar Variables">
            <P>
              A <strong>scalar</strong> is a plain text value — the most common type.
              The placeholder is replaced by the corresponding string from the JSON payload.
            </P>
            <CodeBlock language="Word template">{`Invoice number: {{invoiceNumber}}
Date: {{invoiceDate}}
Client: {{clientName}}`}</CodeBlock>
            <CodeBlock language="JSON payload">{`{
  "invoiceNumber": "INV-2024-001",
  "invoiceDate": "January 15, 2025",
  "clientName": "Acme Corporation"
}`}</CodeBlock>
            <P>
              If a scalar variable appears in the payload but its value is empty, the
              placeholder is replaced with an empty string. If the variable is missing from
              the payload entirely, it is replaced with empty string and a warning is logged.
            </P>
          </Section>

          <Section id="nested" title="Nested Objects">
            <P>
              Use <strong>dot notation</strong> to access fields inside nested JSON objects.
            </P>
            <CodeBlock language="Word template">{`Ship to:
{{shipping.recipientName}}
{{shipping.addressLine1}}
{{shipping.city}}, {{shipping.state}} {{shipping.zip}}`}</CodeBlock>
            <CodeBlock language="JSON payload">{`{
  "shipping": {
    "recipientName": "Alice Smith",
    "addressLine1": "123 Main St",
    "city": "Springfield",
    "state": "IL",
    "zip": "62701"
  }
}`}</CodeBlock>
            <P>
              Nesting can go multiple levels deep: <Code>{'{{a.b.c}}'}</Code> reads
              the key <Code>c</Code> inside <Code>b</Code> inside <Code>a</Code>.
            </P>
          </Section>

          <Section id="loops" title="Repeating Sections">
            <P>
              A <strong>repeating section</strong> (loop) renders its body once for each
              element in a JSON array. Use <Code>{'{{#each collection}}'}</Code> to open the
              section and <Code>{'{{/each}}'}</Code> to close it.
            </P>
            <P>
              Inside the section, access item fields via the default alias{' '}
              <Code>item</Code> (e.g. <Code>{'{{item.description}}'}</Code>). To use a
              different alias, use the <Code>{'{{#for collection as alias}}'}</Code> form.
            </P>

            <SubSection id="loops-each" title="#each — default alias">
              <CodeBlock language="Word template">{`{{#each lineItems}}
  {{item.description}}    {{item.qty}}    {{item.unitPrice}}    {{item.amount}}
{{/each}}`}</CodeBlock>
              <CodeBlock language="JSON payload">{`{
  "lineItems": [
    { "description": "Widget", "qty": "10", "unitPrice": "$5.00",  "amount": "$50.00"  },
    { "description": "Gadget", "qty":  "2", "unitPrice": "$25.00", "amount": "$50.00"  },
    { "description": "Doohickey", "qty": "1", "unitPrice": "$8.00", "amount": "$8.00" }
  ]
}`}</CodeBlock>
              <P>
                The three lines above would produce three rows in the PDF — one per array element.
                Any Word formatting on the body paragraph (font, bold, table row, etc.) is
                replicated for each iteration.
              </P>
            </SubSection>

            <SubSection id="loops-for" title="#for — custom alias">
              <CodeBlock language="Word template">{`{{#for attendees as person}}
  {{person.name}} — {{person.email}}
{{/for}}`}</CodeBlock>
              <CodeBlock language="JSON payload">{`{
  "attendees": [
    { "name": "Alice",   "email": "alice@example.com"   },
    { "name": "Bob",     "email": "bob@example.com"     },
    { "name": "Charlie", "email": "charlie@example.com" }
  ]
}`}</CodeBlock>
            </SubSection>

            <SubSection id="loops-scope" title="Item fields, counters and indexes">
              <P>
                Inside a loop, a field without an alias is looked up on the current item first, then
                on each enclosing loop's item, then on the root payload — so{' '}
                <Code>{'{{description}}'}</Code> works as well as <Code>{'{{item.description}}'}</Code>,
                and a nested <Code>{'{{#each lines}}'}</Code> finds the current item's{' '}
                <Code>lines</Code>.
              </P>
              <CodeBlock language="Word template">{`{{#each lineItems}}
  {{#if $first}}Items ({{$count}}){{/if}}
  {{$number}}. {{description}} — {{currency}}
{{/each}}
Most recent: {{lineItems[last].description}}`}</CodeBlock>
              <P>
                <Code>{'{{$index}}'}</Code> (from 0), <Code>{'{{$number}}'}</Code> (from 1),{' '}
                <Code>{'{{$count}}'}</Code>, <Code>$first</Code> and <Code>$last</Code> describe the
                innermost loop. Any path can index an array with <Code>[0]</Code>,{' '}
                <Code>[first]</Code> or <Code>[last]</Code>; an index past the end is treated as a
                missing field.
              </P>
              <P>
                A loop over an object repeats once per property, in payload order: <Code>{'{{$key}}'}</Code> is
                the property name and <Code>{'{{$this}}'}</Code> its value; when the value is an object, its fields
                read by name.
              </P>
              <CodeBlock language="Word template">{`{{#each prices}}
  {{$key}}: {{$this | currency:EUR}}
{{/each}}`}</CodeBlock>
              <P>
                Formatters after the collection order it: <Code>| sort:field</Code> ascending,{' '}
                <Code>| sortdesc:field</Code> descending, and <Code>| distinct:field</Code> repeats once per distinct
                value, in that order, with the first item of each. Inside a distinct loop,{' '}
                <Code>{'{{cars | where:brand,$brand | map:qty | sum}}'}</Code> totals the current item's group.
              </P>
              <CodeBlock language="Word template">{`{{#each cars | distinct:brand}}
  {{brand}}: {{cars | where:brand,$brand | map:qty | sum}} cars
{{/each}}`}</CodeBlock>
            </SubSection>

            <SubSection id="loops-pagebreak" title="Page break between iterations">
              <P>
                Place a <Code>{'{{#pageBreak}}'}</Code> inside the loop body to insert a page
                break after each item (useful for generating one page per customer, etc.).
              </P>
              <CodeBlock language="Word template">{`{{#each reports}}
  {{item.title}}
  {{item.body}}
  {{#pageBreak}}
{{/each}}`}</CodeBlock>
            </SubSection>

            <SubSection id="loops-inline" title="Inline loops — inside a sentence">
              <P>
                A <Code>{'{{#each}}'}</Code> or <Code>{'{{#for}}'}</Code> in the middle of a paragraph, closed by{' '}
                <Code>{'{{/each}}'}</Code> in the <em>same</em> paragraph, repeats just that part of the sentence. Fields,
                counters and aliases work as in a block loop; <Code>{'{{#unless $last}}'}</Code> keeps a separator off the
                last item.
              </P>
              <CodeBlock language="Word template">{`Tags: {{#each tags}}{{name}} ({{qty}}){{#unless $last}}, {{/unless}}{{/each}}.`}</CodeBlock>
            </SubSection>

            <SubSection id="loops-columns" title="Column loops — repeat table columns">
              <P>
                <Code>{'{{#columns collection}}'}</Code> as the first paragraph of a table cell, and{' '}
                <Code>{'{{/columns}}'}</Code> as the last paragraph of the same cell or a later one in that row, repeat
                those columns once per item, in every row of the table. Each copy reads its own item, so a header cell
                and the cells under it line up. The repeated columns share the width the original ones had.{' '}
                <Code>{'{{#columns collection as m}}'}</Code> names the alias; sorting (<Code>| sort:field</Code>) works
                as for <Code>#each</Code>. A merged cell that crosses the loop&apos;s edge stops it repeating.
              </P>
              <CodeBlock language="Word table">{`| Product     | {{#columns months}}     | Total     |
|             | {{name}}                |           |
|             | {{/columns}}            |           |
| {{product}} | {{sales}}               | {{total}} |`}</CodeBlock>
            </SubSection>
          </Section>

          <Section id="conditionals" title="Conditionals">
            <P>
              Wrap content in <Code>{'{{#if condition}}'}</Code> / <Code>{'{{/if}}'}</Code> to
              show or hide it based on a boolean value in the payload.
            </P>

            <SubSection id="cond-if" title="#if — show when true">
              <CodeBlock language="Word template">{`{{#if showDiscount}}
  Discount applied: {{discountAmount}}
{{/if}}`}</CodeBlock>
              <CodeBlock language="JSON payload">{`{
  "showDiscount": true,
  "discountAmount": "10%"
}`}</CodeBlock>
            </SubSection>

            <SubSection id="cond-unless" title="#unless — show when false">
              <CodeBlock language="Word template">{`{{#unless isPaid}}
  PAYMENT DUE: {{amountDue}}
{{/unless}}`}</CodeBlock>
            </SubSection>

            <SubSection id="cond-else" title="#else — alternate branch">
              <CodeBlock language="Word template">{`{{#if memberStatus}}
  Thank you for being a valued member!
{{else}}
  Join today and save 20%.
{{/if}}`}</CodeBlock>
            </SubSection>

            <SubSection id="cond-inline" title="Inline conditionals — inside a sentence">
              <CodeBlock language="Word template">{`Dear {{#if title}}{{title}} {{else}}Customer {{/if}}{{name}},

{{#each items}}
{{name}}{{#unless $last}},{{else}}.{{/unless}}
{{/each}}`}</CodeBlock>
              <P>
                <Code>{'{{#if}}'}</Code>, <Code>{'{{#unless}}'}</Code> and <Code>{'{{else}}'}</Code> also work
                in the middle of a paragraph, closed by <Code>{'{{/if}}'}</Code> (or <Code>{'{{/unless}}'}</Code>) in the <em>same</em> paragraph.
                They test values exactly like the block form (including <Code>$first</Code> / <Code>$last</Code>
                inside a loop), can be nested, and keep the formatting of the text inside them. A tag
                left unclosed in its paragraph is ignored — its text is removed and the content around it is kept.
              </P>
            </SubSection>

            <Callout type="info">
              Conditional blocks can be nested and can contain loops. Loop blocks can also
              contain nested conditionals. A conditional or loop that spans paragraphs{' '}
              <em>must</em> have its tags as the sole content of their paragraphs; one inside a
              sentence must open and close in that same paragraph.
            </Callout>
          </Section>

          <Section id="reserved" title="Reserved Keywords">
            <P>
              The following names are <strong>reserved</strong> — they trigger special
              behavior rather than simple substitution. Do not use them as regular variable names.
            </P>

            <SubSection id="reserved-directives" title="Structural directives">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{#each collection}}"       description="Opens a repeating section. The body repeats once per element in the array named collection. Sub-fields accessed as {{item.field}} using the default 'item' alias." />
                <Keyword word="{{#for collection as alias}}" description="Same as #each but lets you choose the loop alias. Useful when nesting loops: {{#for orders as order}}, {{#for order.items as line}}." />
                <Keyword word="{{/each}}"                  description="Closes an #each or #for block. Aliases {{/for}} and {{/loop}} are also accepted." />
                <Keyword word="{{#columns collection}} … {{/columns}}" description="Inside a table: repeats the columns from the cell it opens to the cell it closes, once per element, in every row." />
                <Keyword word="{{#if condition}}"          description="Renders the body only when condition is truthy (true, non-empty string, non-zero number) in the payload." />
                <Keyword word="{{#unless condition}}"      description="Renders the body only when condition is falsy. Logical inverse of #if." />
                <Keyword word="{{else}}"                   description="Separates the then-branch from the else-branch inside an #if or #unless block." />
                <Keyword word="{{/if}}"                    description="Closes an #if or #unless block. Alias {{/unless}} is also accepted." />
              </div>
            </SubSection>

            <SubSection id="reserved-other" title="Other reserved words">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{$index}} {{$number}} {{$count}}" description="Inside a loop: the current item's 0-based index, its 1-based number, and the number of items. Refers to the innermost loop; not read from the payload and not listed as a payload field." />
                <Keyword word="$first / $last" description="Inside a loop: true on the first / last item, for use as {{#if $first}} or {{#unless $last}}." />
                <Keyword word="{{#pageBreak}}" description="Forces a page break at this position in the document. Can be placed anywhere — inside or outside loops. Case-insensitive, so {{#PageBreak}} also works. The bare {{pageBreak}} (no #) is no longer recognized as a directive — every directive is # prefixed." />
              </div>
            </SubSection>

            <Callout type="tip">
              All directive paragraphs (<Code>{'{{#each}}'}</Code>, <Code>{'{{/each}}'}</Code>,
              etc.) are stripped from the output entirely — they will not appear in the rendered PDF.
            </Callout>
          </Section>

          <Section id="formatters" title="Formatters">
            <P>
              Append a pipe (<Code>|</Code>) and a formatter name inside any variable placeholder
              to transform the value before it is written to the document.
              Chain multiple formatters by adding more pipes.
            </P>
            <CodeBlock language="Syntax">{`{{variableName | formatterName}}
{{variableName | formatterName:arg}}
{{variableName | formatterName:arg1,arg2}}
{{variableName | formatter1 | formatter2}}`}</CodeBlock>

            <P>
              Arguments are separated by commas. Wrap an argument in quotes when it contains a comma,
              pipe or colon of its own — number and date patterns usually do.
            </P>
            <CodeBlock language="Quoting arguments">{`{{total | number:'#,##0.00'}}
{{signed | date:'MMMM d, yyyy'}}
{{at | date:'HH:mm'}}`}</CodeBlock>

            <P>
              An argument written <Code>$name</Code> reads the field <Code>name</Code> instead of being
              literal text, in the same scope as the placeholder: inside a loop, the current item&apos;s field
              first. Loop counters work too (<Code>$index</Code>, <Code>$number</Code>). When the payload has no
              such field the argument stays literal, so <Code>{'{{total | prepend:$}}'}</Code> still prints a
              dollar sign.
            </P>
            <CodeBlock language="Field-valued arguments">{`{{price | multiply:$qty}}
{{due | datediff:$issued,days}}
{{base | add:$index}}`}</CodeBlock>

            <P>
              Every formatter name is lowercase with no separators, and every argument is optional
              with a documented default. A formatter that cannot act on the value — a number
              formatter on text, a date formatter on something unparseable — passes the value
              through untouched rather than producing an error or <Code>NaN</Code>.
            </P>

            <SubSection id="fmt-string" title="Text formatters">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{name | upper}}"            description="UPPERCASE. Alias: uppercase." />
                <Keyword word="{{name | lower}}"            description="lowercase. Alias: lowercase." />
                <Keyword word="{{name | title}}"            description="Title Cases Every Word. Alias: titlecase." />
                <Keyword word="{{name | ucfirst}}"          description="Capitalizes only the first character, leaving the rest as-is. Alias: capitalize." />
                <Keyword word="{{name | sentencecase}}"     description="Capitalizes the first character and lowercases the rest." />
                <Keyword word="{{name | swapcase}}"         description="Inverts the case of every character." />
                <Keyword word="{{name | camelcase}}"        description="firstNameLast — splits on spaces, punctuation and existing humps." />
                <Keyword word="{{name | pascalcase}}"       description="FirstNameLast." />
                <Keyword word="{{name | kebabcase}}"        description="first-name-last." />
                <Keyword word="{{name | snakecase}}"        description="first_name_last." />
                <Keyword word="{{name | trim}}"             description="Removes leading and trailing whitespace." />
                <Keyword word="{{name | trimstart}}"        description="Removes leading whitespace only." />
                <Keyword word="{{name | trimend}}"          description="Removes trailing whitespace only." />
                <Keyword word="{{code | padstart:8,0}}"     description="Pads to N characters on the left. Second argument is the pad character (default space)." />
                <Keyword word="{{code | padend:8,.}}"       description="Pads to N characters on the right." />
                <Keyword word="{{name | truncate:50}}"      description="Truncates to N characters (default 100), appending … if it was longer." />
                <Keyword word="{{name | truncatewords:10}}" description="Truncates to N words (default 10), appending …" />
                <Keyword word="{{name | substr:0,10}}"      description="Slice from a start index, optionally for N characters. A negative start counts back from the end." />
                <Keyword word="{{text | replace:old,new}}"  description="Replaces every occurrence of the first argument with the second." />
                <Keyword word="{{text | replacefirst:old,new}}" description="Replaces only the first occurrence." />
                <Keyword word="{{text | remove:draft}}"     description="Deletes every occurrence of the argument." />
                <Keyword word="{{name | prepend:Dr. }}"     description="Puts the argument in front of the value." />
                <Keyword word="{{name | append: Jr.}}"      description="Puts the argument after the value." />
                <Keyword word="{{name | unaccent}}"         description="Strips diacritics — Éloïse becomes Eloise." />
                <Keyword word="{{name | reverse}}"          description="Reverses the characters, or the elements of a JSON array field." />
                <Keyword word="{{csv | split:;}}"           description="Splits into a JSON array (default separator is a comma) so the array formatters can be chained after it." />
                <Keyword word="{{name | length}}"           description="Character count, or element count for a JSON array field. Alias: size." />
                <Keyword word="{{nickname | default:N/A}}"  description="Falls back to the argument when the value is empty." />
              </div>
            </SubSection>

            <SubSection id="fmt-date" title="Date formatters">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{dob | date:'MMMM d, yyyy'}}" description="Custom .NET date format string. Parses ISO 8601, RFC dates, etc. Quote the pattern if it contains a comma or colon." />
                <Keyword word="{{dob | dateshort}}"           description="Shorthand: M/d/yyyy — e.g. 1/20/2025. Alias: date-short." />
                <Keyword word="{{dob | datelong}}"            description="Shorthand: MMMM d, yyyy — e.g. January 20, 2025. Alias: date-long." />
                <Keyword word="{{dob | datetime}}"            description="Shorthand: M/d/yyyy h:mm tt. Alias: date-time." />
                <Keyword word="{{dob | time}}"                description="Time only: h:mm tt — e.g. 2:05 PM." />
                <Keyword word="{{dob | year}}"                description="The year — e.g. 2025." />
                <Keyword word="{{dob | month}}"               description="The full month name — e.g. January." />
                <Keyword word="{{dob | day}}"                 description="The day number — e.g. 20." />
                <Keyword word="{{dob | weekday}}"             description="The full day name — e.g. Monday." />
                <Keyword word="{{due | dateadd:30,days}}"     description="Shifts the date forward. Units: years, months, weeks, days, hours, minutes, seconds (default days). Chain a date formatter after it." />
                <Keyword word="{{due | datesubtract:1,month}}" description="Shifts the date backward, same units." />
              </div>
            </SubSection>

            <SubSection id="fmt-number" title="Number & currency formatters">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{price | currency:USD}}"      description="Formats as $1,234.56. Also EUR (€), GBP (£), JPY (¥). With no code, the render locale's currency (USD when the render has no locale)." />
                <Keyword word="{{amount | number:N2}}"        description=".NET numeric format string — N0, N2, or a custom pattern like '#,##0.00' (quote it, the comma is part of the pattern)." />
                <Keyword word="{{amount | fixed:2}}"          description="Always N decimal places — 1.5 becomes 1.50. Default 2." />
                <Keyword word="{{rate | percent:1}}"          description="Formats as a percentage with N decimal places — 0.256 becomes 25.6 %. Default 0." />
                <Keyword word="{{rank | ordinal}}"            description="1 becomes 1st, 22 becomes 22nd." />
                <Keyword word="{{size | filesize}}"           description="Byte count to a human-readable size — 1536 becomes 1.5 KB." />
                <Keyword word="{{score | round:2}}"           description="Rounds to N decimal places. Default 0." />
                <Keyword word="{{score | floor}}"             description="Rounds down to the nearest integer." />
                <Keyword word="{{score | ceil}}"              description="Rounds up to the nearest integer." />
                <Keyword word="{{score | int}}"               description="Discards the fractional part (truncates toward zero)." />
                <Keyword word="{{delta | abs}}"               description="Absolute value." />
                <Keyword word="{{area | sqrt}}"               description="Square root. Negative values pass through." />
                <Keyword word="{{count | add:1}}"             description="Adds a constant." />
                <Keyword word="{{count | subtract:1}}"        description="Subtracts a constant. Alias: sub." />
                <Keyword word="{{qty | multiply:1.2}}"        description="Multiplies by a constant. Alias: mul." />
                <Keyword word="{{total | divide:12}}"         description="Divides by a constant; dividing by zero leaves the value unchanged. Alias: div." />
                <Keyword word="{{index | mod:2}}"             description="Remainder after division." />
                <Keyword word="{{base | pow:2}}"              description="Raises to the given power." />
                <Keyword word="{{price | locale:de-DE | currency:EUR}}" description="Pins the culture for the formatters after it, whatever the render locale: 1.234,50 €. locale:invariant restores the default output. See Localization." />
              </div>
            </SubSection>

            <SubSection id="fmt-agg" title="Array formatters">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{prices | sum}}"      description="Sums all numeric elements in a JSON array field." />
                <Keyword word="{{prices | avg}}"      description="Mean of all numeric elements." />
                <Keyword word="{{prices | min}}"      description="Smallest numeric element." />
                <Keyword word="{{prices | max}}"      description="Largest numeric element." />
                <Keyword word="{{prices | count}}"    description="Number of elements." />
                <Keyword word="{{tags | first}}"      description="First element, as a scalar." />
                <Keyword word="{{tags | last}}"       description="Last element, as a scalar." />
                <Keyword word="{{tags | sort}}"       description="Sorts numerically when every element is a number, alphabetically otherwise. Returns an array." />
                <Keyword word="{{tags | unique}}"     description="Removes duplicates, keeping the first of each. Returns an array." />
                <Keyword word="{{cars | where:brand,Volvo}}" description="Keeps the elements whose field equals the value (numbers compare numerically). With $field the value is read from the payload: where:brand,$brand. Returns an array." />
                <Keyword word="{{tags | join:', '}}"  description="Joins the elements with the given separator (default ', ')." />
                <Keyword word="{{payload | json}}"    description="Pretty-prints the value as indented JSON." />
              </div>
            </SubSection>

            <SubSection id="fmt-image" title="Image, barcode & chart formatters">
              <div style={{ borderTop: `1px solid ${colors.border}` }}>
                <Keyword word="{{logo | image:200x80}}"   description="Renders the field as an inline image. Value must be a base64 data URI (data:image/png;base64,...), raw base64, or an http(s) URL. Width×height in points." />
                <Keyword word="{{url | qr:150}}"          description="Generates a QR code from the field value. Size is the square dimension in points." />
                <Keyword word="{{sku | barcode:150x50}}"  description="Code 128 barcode. Also ean13 and datamatrix." />
                <Keyword word="{{series | bar:300x200}}"  description="Bar chart from a JSON array of numbers or {label,value} objects. Also line and pie." />
                <Keyword word="{{fragment | html}}"       description="Parses the value as an HTML fragment and injects it as real document blocks." />
              </div>
            </SubSection>

            <SubSection id="fmt-migration" title="Migrating from Carbone, Docmosis or Handlebars">
              <P>
                Uploading a .docx on the Templates page detects the source syntax and rewrites its
                formatting calls into the formatters above automatically. This table is the mapping
                it applies, for reference when checking a converted template.
              </P>
              <P>
                Carbone templates are converted structurally as well. The row or paragraph holding{' '}
                <Code>{'{d.items[i].name}'}</Code> is the repeated pattern and the next one holding{' '}
                <Code>{'{d.items[i+1].name}'}</Code> only marks its end, so the importer wraps the pattern in{' '}
                <Code>{'{{#each items}}'}</Code> (splitting a table around its repeated rows), removes the
                marker, and makes the tags inside relative to the item. Inline conditions become inline{' '}
                <Code>{'{{#if}}'}</Code> expressions; <Code>showBegin</Code>/<Code>showEnd</Code> and{' '}
                <Code>drop()</Code> become conditions around whole paragraphs, rows or tables.
              </P>
              <CodeBlock language="Carbone">{`{d.name} / {d.customer.name}         → {{name}} / {{customer.name}}
{d.items[2].name} / {d.items[i=-1]}  → {{items[2].name}} / {{items[last]}}
{c.company}  (request complement)    → {{$complement.company}}
{d.items[i].name} … {d.items[i+1]}   → {{#each items}} the rows/paragraphs {{/each}}
{d.a[i].b[i].x} … {d.a[i].b[i+1].x}  → nested {{#each a}} {{#each b}} {{x}}
{d.tags[i]}                          → {{$this}}  (a plain value in the loop)
{d.tags[i]}, {d.tags[i+1]}  (inline) → {{tags | join:', '}}
{d.p[i].a} ({d.p[i].b}), {d.p[i+1].a} → {{#each p}}{{a}} ({{b}}){{#unless $last}}, {{/unless}}{{/each}}
table cells [i] | [i+1] (horizontal)  → {{#columns x}} … {{/columns}}, the [i+1] column removed
{d.prices[i].att}: {d.prices[i].val} → {{#each prices}}{{$key}}: {{$this}}  (an object's properties)
[i, qty > 1, type='car']             → {{#each items}}{{#if (qty > 1) && (type == 'car')}}…
[i, i < 2] / [i, i!=-1]              → {{#if $index < 2}} / {{#if !$last}}
[price, i] … [price+1, i+1]          → {{#each items | sort:price}}
[brand] … [brand+1]  (distinct)      → {{#each cars | distinct:brand}}
{d.items[i].x:count}                 → {{$number}}
{d.items[].price:aggSum}             → {{items | map:price | sum}}  (aggAvg/Min/Max/Count)
{d.cars[i].qty:aggSum(.brand)}       → {{cars | where:brand,$brand | map:qty | sum}}  (in a loop)

{d.status:ifEQ(paid):show(Paid):elseShow(Due)}
                                     → {{#if status == 'paid'}}Paid{{else}}Due{{/if}}
{d.qty:ifGT(10):and(.vip):ifEQ(true):show(Rush)}
                                     → {{#if (qty > 10) && (vip == true)}}Rush{{else}}{{qty}}{{/if}}
:ifNE :ifGTE :ifLT :ifLTE            → != >= < <=
:ifIN(x) / :ifNIN(x)                 → {{#if v | contains:x}} / {{#if !(v | contains:x)}}
:ifEM / :ifNEM / :ifTE(number)       → v | isempty / !(v | isempty) / v | istype:number
{d.vip:ifEQ(true):showBegin} … {d.vip:showEnd}
                                     → {{#if vip == true}} … {{/if}}  (hideBegin → #unless)
{d.note:ifEM:drop(p)}  / drop(row)   → paragraph / row wrapped in {{#unless note | isempty}}
{#cust = d.order.customer} {$cust.name} → {{order.customer.name}}
{o.lang=fr}                          → removed; renders in fr when the request names no lang
{t(Hello)} / {d.label:t}             → {{$translations | get:Hello | default:Hello}} / lookup
{d.state:convEnum('ST')}             → {{state | lookup:$enum.ST}}
picture with alt text {d.logo}       → picture with alt text {{logo}}

{d.name:upperCase} / lowerCase       → {{name | upper}} / lower
{d.name:ucFirst} / ucWords           → {{name | ucfirst}} / title
{d.name:substr(0,10)} / slice        → {{name | substr:0,10}}
{d.name:padl(8,'0')} / padr          → {{name | padstart:8,0}} / padend
{d.name:ellipsis(50)}                → {{name | truncate:50}}
{d.name:print('x')} / unaccent / len → {{name | print:x}} / unaccent / length
{d.total:formatN(2)}                 → {{total | number:N2}}
{d.total:formatC(2, 'EUR')}          → {{total | currency:EUR}}  (no code: the render language's currency)
{d.total:add(5)} / sub/mul/div/mod   → {{total | add:5}} / subtract / multiply / divide / mod
{d.total:round(2)} / toFixed / int   → {{total | round:2}} / fixed / int
{d.signed:formatD('YYYY-MM-DD')}     → {{signed | date:yyyy-MM-dd}}
{d.due:addD(30,'day')} / subD        → {{due | dateadd:30,day}} / datesubtract
{d.due:startOfD('month')} / endOfD   → {{due | startof:month}} / endof
{d.due:diffD('2026-12-25','days')}   → {{due | datediff:2026-12-25,days}}
{d.price:mul(.qty)} / diffD(.end)    → {{price | multiply:$qty}} / datediff:$end  (a field as argument)
{d.rows:arrayJoin(', ')}             → {{rows | join:', '}}
{d.code:barcode('qrcode')} / ean13   → {{code | qr}} / ean13
{d.body:html}                        → {{body | html}}
{d.nickname:ifEmpty('N/A')}          → {{nickname | default:N/A}}`}</CodeBlock>
              <P>
                Not converted, always with a warning in the import review: loops across table columns where a
                merged cell crosses the repeated columns' edge (the items are joined into one cell), a partitioned
                aggregate outside a loop, <Code>convCRLF</Code> and <Code>arrayMap</Code>. Charts, <Code>set()</Code>, <Code>cumSum</Code>,{' '}
                <Code>bindColor</Code> and <Code>appendFile</Code> are not supported.
              </P>
              <P>
                Docmosis templates are converted structurally, not just tag by tag: DocMorphia needs a
                block tag alone in its paragraph and repeats table rows by splitting the table, so the
                importer rearranges paragraphs and tables to match. Custom field delimiters (Docmosis'
                fieldDelimPrefix / fieldDelimSuffix) can be supplied as <Code>delimiterPrefix</Code> and{' '}
                <Code>delimiterSuffix</Code> on the import.
              </P>
              <CodeBlock language="Docmosis">{`<<name>> / <<customer.name>>          → {{name}} / {{customer.name}}
<<hotel[0]>> / <<hotel[F]>> / [L]      → {{hotel[0]}} / {{hotel[first]}} / [last]
<<rs_items>> … <<es_>>                 → {{#each items}} … {{/each}}
<<rr_lines>> … <<er_>>   (table rows)  → table split: {{#each lines}} | the rows | {{/each}}
<<cr_flag>> … <<er_>>    (table rows)  → table split: {{#if flag}} | the rows | {{/if}}
<<cs_flag>> … <<else>> … <<es_>>       → {{#if flag}} … {{else}} … {{/if}}
<<cs_{!flag}>> / cs_{isBlank(x)}       → {{#unless flag}} / {{#unless x}}
<<else_other>>                         → {{else}}{{#if other}}  (+ one extra {{/if}} at the end)
<<op:addr2>>                           → {{#if addr2}} paragraph-with-{{addr2}} {{/if}}
<<pageBreak>>                          → {{#pageBreak}}
<<pageBreakNotLast>>                   → {{#unless $last}} {{#pageBreak}} {{/if}}
<<$num>> / <<$idx>> / <<$size>>        → {{$number}} / {{$index}} / {{$count}}
<<$top.company>> / <<$parent.name>>    → {{company}} / {{name}}  (loop scope looks outward)
<<html:bio>>                           → {{bio | html}}
<<## comment ##>> / <</* comment */>>  → removed
MERGEFIELD customerName                → {{customerName}}
picture bookmarked img_logo            → picture with alt text {{logo}}

<<{toUpperCase(name)}>> / toLowerCase  → {{name | upper}} / lower
<<{titleCase(name)}>> / toSentence     → {{name | title}} / sentencecase
<<{substring(name,0,5)}>>              → {{name | substr:0,5}}
<<{left(name,3)}>> / right(name,3)     → {{name | substr:0,3}} / substr:-3
<<{replaceStr(text,'a','b')}>>         → {{text | replace:a,b}}
<<{replaceFirst(text,'a','b')}>>       → {{text | replacefirst:a,b}}
<<{ifBlank(name,'N/A')}>>              → {{name | default:N/A}}
<<{numFormat(total,'#,##0.00')}>>      → {{total | number:'#,##0.00'}}
<<{numFormat(total,'$#,##0.00')}>>     → {{total | currency:USD}}
<<{numFormat(rate,'#0.0%')}>>          → {{rate | percent:1}}
<<{dateFormat(signed,'yyyy-MM-dd')}>>  → {{signed | date:yyyy-MM-dd}}
<<{dateAdd(due,30,'days')}>>           → {{due | dateadd:30,days}}
<<{round(total,2)}>> / abs / ceil      → {{total | round:2}} / abs / ceil
<<{ordinal(rank)}>>                    → {{rank | ordinal}}`}</CodeBlock>
              <P>
                Docmosis constructs with no DocMorphia equivalent are kept visibly or removed, always
                with a warning in the import review: conditions that compare values
                (<Code>{'<<cs_{total>100}>>'}</Code> tests whether <Code>total</Code> has a value — compute
                a boolean in the payload), repeat directives (<Code>:sort</Code>, <Code>:filter</Code>,{' '}
                <Code>:group</Code>, <Code>:stepN</Code>), range specifiers (<Code>[*]</Code>,{' '}
                <Code>[1-3]</Code>), conditional columns (<Code>cc_</Code>), template variables
                (<Code>{'<<$x=…>>'}</Code>, <Code>$this</Code>, <Code>var_</Code>), <Code>link:</Code>{' '}
                (the URL prints as text), <Code>fc:</Code>, <Code>barcode:</Code>/<Code>qrcode:</Code>{' '}
                settings, <Code>ref:</Code>/<Code>refLookup:</Code>/<Code>coordinator:</Code> and column breaks.
              </P>
              <CodeBlock language="Handlebars">{`{{uppercase name}}              → {{name | upper}}
{{capitalize name}}             → {{name | ucfirst}}
{{titleize name}}               → {{name | title}}
{{dashcase name}} / snakecase   → {{name | kebabcase}} / snakecase
{{truncate title 20}}           → {{title | truncate:20}}
{{truncateWords body 10}}       → {{body | truncatewords:10}}
{{replace body 'a' 'b'}}        → {{body | replace:a,b}}
{{toFixed total 2}}             → {{total | fixed:2}}
{{addCommas total}}             → {{total | number:'#,##0.###'}}
{{bytes size}}                  → {{size | filesize}}
{{add count 1}} / subtract etc. → {{count | add:1}} / subtract / multiply / divide
{{dateFormat signed 'YYYY-MM-DD'}} → {{signed | date:yyyy-MM-dd}}
{{join tags ', '}}              → {{tags | join:', '}}
{{default nickname 'N/A'}}      → {{nickname | default:N/A}}
{{json payload}}                → {{payload | json}}`}</CodeBlock>
              <P>
                Calls with no equivalent are reported as warnings in the import review rather than
                being silently discarded. Handlebars comparison helpers (<Code>eq</Code>,{' '}
                <Code>gt</Code>, …) have no formatter form — rewrite them as{' '}
                <Code>{'{{#if}}'}</Code> blocks. Carbone's <Code>convCurr</Code> performs live
                currency conversion, and Docmosis expressions that combine fields
                (<Code>{"<<{amount * qty}>>"}</Code>, <Code>numToText</Code>, <Code>map</Code>) need to be
                computed in the payload instead.
              </P>
            </SubSection>

            <CodeBlock language="Example — invoice with formatters">{`Amount due: {{total | currency:USD}}
Issue date: {{issueDate | datelong}}
Due date:   {{issueDate | dateadd:30,days | date:M/d/yyyy}}
Client:     {{clientName | upper}}
Ref:        {{ref | truncate:20}}
Line items: {{items | count}} — totalling {{amounts | sum | currency:USD}}
Tags:       {{tags | unique | sort | join:', '}}`}</CodeBlock>
          </Section>

          <Section id="localization" title="Localization">
            <P>
              One layout, many languages. The document's own static text is the source language; each locale you add
              supplies a translation of every string. <code>{'{{variables}}'}</code>, loops and conditionals are not translated:
              they stay in the translated sentence and fill in exactly as in the source. Open <strong>Translations</strong> in the
              editor to set the template's default language, add locales, translate inline, draft the missing strings with
              AI, and import or export a CSV or JSON file for a translator. Translations are saved with the version and go
              Live when it is promoted.
            </P>
            <P>
              A string is one paragraph. Text in a different format (a bold word, a link) appears as a numbered tag,
              <code>{'Pay <1>now</1>'}</code>, and a tab, line break or field as <code>{'<2/>'}</code>. A link whose words have
              different formats keeps them as tags inside its own: <code>{'Read <1>the <2>full</2> terms</1>'}</code>; a translation
              keeps each inner tag inside the link's tag. A translation must keep
              every variable and tag exactly once (it can move them); one that does not renders in the default language,
              with a warning. Strings are matched by their text: moving or restyling a paragraph keeps its translation,
              rewording it retires the translation and the string shows as missing.
            </P>
            <P>
              Besides paragraphs, a picture's alt text, the document title and subject, and comment text are strings too.
              Translated alt text is written to the output's alt text (Word, HTML, ODT, PowerPoint, Excel), the title and
              subject to the document properties, and comments appear translated in Word output, the one output that keeps them.
            </P>
            <P>
              <strong>Same words, different translation.</strong> Identical text shares one translation, so "Total" is
              translated once. Where one place needs different words, open the string's <code>×N</code> occurrence list in
              Translations and choose <strong>Translate this occurrence differently</strong>. An occurrence is identified by the
              heading it sits under (or the header, footer, footnote or comment it is in) and which copy under that heading it
              is, so editing other parts of the document keeps it. If that heading is reworded or the copy is removed, the
              occurrence's own translation stops applying, the shared one is used, and the render warns. Exports list these
              rows with a <code>context</code> column.
            </P>
            <P>
              <strong>Choosing the language.</strong> The render request's <code>locale</code> field wins
              (<code>"locale": "fr-CA"</code>); otherwise a top-level <code>locale</code> key in the payload of a localized template
              (the key can be renamed per template); otherwise the template's default language. Each string falls back from
              <code> fr-CA</code> to <code>fr</code> to the default text, and the render's warnings list the strings that had no
              translation. Drafts from Translate with AI render too, with a warning, until someone accepts them. For text that occurs
              more than once, Translate with AI also looks at each place it appears and, only where the context needs different
              wording, drafts a translation for that occurrence, shown with a Draft badge in the expanded list. Translate with AI is on the
              Ultimate plan and paid per use: you tick the languages, see each one's price and the total before anything runs, and
              are charged that quote at most, on your next invoice. It never runs on its own.
            </P>
            <P>
              <strong>Formatters follow the locale.</strong> Dates, numbers, currency, file sizes, ordinals and upper/lower case use the
              render locale: <code>{'{{d | datelong}}'}</code> is <em>19 octobre 2026</em> in fr-FR, <code>{'{{n | number:N2}}'}</code> is
              <em> 1.234,50</em> in de-DE. Arithmetic and array formatters stay culture-neutral so chains keep working.
              <code> {'{{price | locale:de-DE | currency:EUR}}'}</code> pins a culture for one value. With no locale and no
              default language, output is exactly as before.
            </P>
            <CodeBlock>{`POST /api/v1/render/generate
{ "templateVersionId": "...", "locale": "fr-CA", "payloadJson": "{...}" }`}</CodeBlock>
          </Section>

          <Section id="payload" title="Payload Format">
            <P>
              DocMorphia accepts payloads in <strong>JSON</strong> (default) or <strong>XML</strong>.
            </P>
            <P>
              For JSON, values are strings, booleans (for conditionals), or arrays (for loops).
              Use formatters (see above) to reformat dates, numbers, and currencies inline —
              or supply pre-formatted strings.
            </P>
            <CodeBlock language="JSON — recommended">{`{
  "recipient": "Alice Smith",
  "orderDate": "January 15, 2025",
  "isPremium": true,
  "items": [
    { "name": "Widget", "price": "$9.99" },
    { "name": "Gadget", "price": "$24.99" }
  ],
  "total": "$34.98"
}`}</CodeBlock>
            <P>
              For XML payloads, wrap values in a root <Code>{'<data>'}</Code> element. Arrays
              use repeated sibling elements with the same tag name as the collection key:
            </P>
            <CodeBlock language="XML — alternative">{`<data>
  <recipient>Alice Smith</recipient>
  <orderDate>January 15, 2025</orderDate>
  <isPremium>true</isPremium>
  <items>
    <name>Widget</name>
    <price>$9.99</price>
  </items>
  <items>
    <name>Gadget</name>
    <price>$24.99</price>
  </items>
  <total>$34.98</total>
</data>`}</CodeBlock>
          </Section>

          <Section id="examples" title="Examples">

            <SubSection id="ex-invoice" title="Invoice with line items">
              <P>A complete invoice template demonstrating scalars, a loop, and page formatting:</P>
              <CodeBlock language="Word template structure">{`                          INVOICE

Invoice #:  {{invoiceNumber}}            Date: {{invoiceDate}}
                                         Due:  {{dueDate}}

Bill To                                  From
{{clientName}}                           {{companyName}}
{{clientAddress}}


Description            Qty    Unit Price    Amount
──────────────────────────────────────────────────
{{#each lineItems}}
{{item.description}}   {{item.qty}}   {{item.unitPrice}}   {{item.amount}}
{{/each}}
──────────────────────────────────────────────────
                                         Subtotal: {{subtotal}}
                                         Tax:      {{tax}}
                                         Total:    {{total}}`}</CodeBlock>
              <CodeBlock language="JSON payload">{`{
  "invoiceNumber": "INV-2024-042",
  "invoiceDate":   "January 15, 2025",
  "dueDate":       "February 15, 2025",
  "clientName":    "Acme Corporation",
  "clientAddress": "123 Main St, Springfield, IL 62701",
  "companyName":   "My Company LLC",
  "lineItems": [
    { "description": "Consulting — Jan",  "qty": "40 hrs", "unitPrice": "$150", "amount": "$6,000" },
    { "description": "Server setup",      "qty":  "1",     "unitPrice": "$500", "amount":   "$500" }
  ],
  "subtotal": "$6,500",
  "tax":        "$520",
  "total":    "$7,020"
}`}</CodeBlock>
            </SubSection>

            <SubSection id="ex-letter" title="Business letter">
              <CodeBlock language="Word template structure">{`{{senderName}}
{{senderAddress}}

{{letterDate}}

{{recipientName}}
{{recipientAddress}}

Re: {{subject}}

Dear {{salutation}},

{{body}}

Sincerely,

{{senderName}}`}</CodeBlock>
              <CodeBlock language="JSON payload">{`{
  "senderName":       "Jane Doe",
  "senderAddress":    "456 Oak Ave, Portland, OR 97201",
  "letterDate":       "January 15, 2025",
  "recipientName":    "John Smith",
  "recipientAddress": "789 Pine Rd, Seattle, WA 98101",
  "subject":          "Partnership Proposal",
  "salutation":       "Mr. Smith",
  "body":             "I am writing to propose a strategic partnership..."
}`}</CodeBlock>
            </SubSection>

            <SubSection id="ex-conditional" title="Conditional content">
              <P>
                A certificate that includes an honours distinction only when earned:
              </P>
              <CodeBlock language="Word template structure">{`Certificate of Completion

This certifies that {{recipientName}} has completed

              {{courseName}}

{{#if withHonours}}
    ✦ With Honours ✦
{{/if}}

Duration:    {{duration}}
Completed:   {{completionDate}}
Issued by:   {{issuedBy}}`}</CodeBlock>
              <CodeBlock language="JSON payload — honours">{`{
  "recipientName":  "Alice Johnson",
  "courseName":     "Advanced Data Analysis",
  "withHonours":    true,
  "duration":       "12 weeks",
  "completionDate": "January 10, 2025",
  "issuedBy":       "DocMorphia University"
}`}</CodeBlock>
              <CodeBlock language="JSON payload — no honours">{`{
  "recipientName":  "Bob Lee",
  "courseName":     "Advanced Data Analysis",
  "withHonours":    false,
  "duration":       "12 weeks",
  "completionDate": "January 12, 2025",
  "issuedBy":       "DocMorphia University"
}`}</CodeBlock>
            </SubSection>
          </Section>

          <Section id="tips" title="Tips & Best Practices">
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {[
                {
                  title: 'One spelling per variable',
                  text: "Pick a casing style (camelCase recommended) and use it consistently. Mixing {{first_name}} and {{firstName}} in the same template will cause a clash error on upload because they normalize to the same key.",
                },
                {
                  title: 'Directives must be alone on their paragraph',
                  text: "Place {{#each}}, {{/each}}, {{#if}}, {{/if}}, and {{#pageBreak}} on their own paragraph line. Mixing a directive and text in the same paragraph (e.g. \"Start: {{#each items}}\") is not supported.",
                },
                {
                  title: 'Use formatters for numbers and dates',
                  text: "Use the pipe formatter syntax to reformat values inline: {{price | currency:USD}}, {{date | date-long}}, {{score | round:2}}. You can also supply pre-formatted strings in your payload — either approach works.",
                },
                {
                  title: 'Use the Preview to iterate fast',
                  text: "Upload the template, open the Preview tab, fill in values, and hit Generate. This round-trip takes seconds and is the fastest way to spot formatting issues without exporting.",
                },
                {
                  title: 'Test edge cases in loops',
                  text: "Always test with zero items, one item, and many items in every loop. Empty arrays skip the loop body entirely — make sure your layout still looks correct with no rows.",
                },
                {
                  title: 'Keep templates platform-neutral',
                  text: "Avoid Windows-only fonts or features. DocMorphia renders on Azure Linux workers using QuestPDF. Stick to common fonts (Arial, Calibri, Times New Roman) or embed fonts in the .docx.",
                },
              ].map(tip => (
                <div key={tip.title} style={{
                  padding: spacing.md,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `4px solid ${colors.primary}`,
                  borderRadius: radius.md,
                }}>
                  <div style={{ fontWeight: 600, color: colors.text, marginBottom: '4px' }}>{tip.title}</div>
                  <div style={{ color: colors.textSecondary, lineHeight: 1.6 }}>{tip.text}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Footer */}
          <div style={{
            marginTop: spacing.xxl,
            paddingTop: spacing.lg,
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <a
              href="/demo"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: fontSize.sm, color: colors.primary, textDecoration: 'none', fontWeight: 500 }}
            >
              ↗ Try it live in the demo
            </a>
            <a
              href="/docs/api"
              style={{ fontSize: fontSize.sm, color: colors.primary, textDecoration: 'none', fontWeight: 500 }}
            >
              API Integration Guide →
            </a>
          </div>
        </main>
      </div>
  )
}
